import React, { useRef, useEffect } from 'react';
import { ThumbnailData } from '../helpers/decodeThumbnail';

interface WaveformThumbnailProps {
    data: ThumbnailData; // absolute peak magnitudes (0..1), one per column
    // Tailwind text-colour class for the bars; the canvas reads it as `currentColor`
    // so the strip can track the row's content colour.
    className?: string;
}

// Renders the browse waveform strip: symmetric vertical bars mirrored around a
// centre line, one bar per canvas column. The stored peaks are at a higher
// resolution than we draw, so we downsample with max-of-peaks (keeps transients)
// to the canvas width — never upsample, so the strip looks right at any width up
// to the stored resolution.
function WaveformThumbnail({ data, className = '' }: WaveformThumbnailProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Size the backing store at the device pixel ratio so the strip stays
        // crisp on retina (otherwise a 1× bitmap gets upscaled and blurs), then
        // scale the context back so all drawing below stays in CSS pixels.
        const dpr = window.devicePixelRatio || 1;
        const width = canvas.clientWidth || 100;
        const height = canvas.clientHeight || 30;
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, width, height);

        if (!data || data.length === 0) return;

        const centerY = height / 2;
        const columns = Math.min(width, data.length);
        const peaksPerColumn = data.length / columns;

        // Resolve the bar colour from the canvas's CSS `color` (set by className),
        // with the original grey as a safety fallback.
        ctx.fillStyle = getComputedStyle(canvas).color || '#4B5563';

        for (let x = 0; x < columns; x++) {
            // Max-of-peaks over the source peaks that fall in this column.
            const start = Math.floor(x * peaksPerColumn);
            const end = Math.floor((x + 1) * peaksPerColumn);
            let peak = 0;
            for (let i = start; i < end && i < data.length; i++) {
                if (data[i] > peak) peak = data[i];
            }

            const barHeight = Math.max(1, peak * height);
            ctx.fillRect(x, centerY - barHeight / 2, 1, barHeight);
        }
    }, [data, className]);

    return (
        <canvas
            ref={canvasRef}
            className={`w-full h-full ${className}`}
            style={{ maxHeight: '24px' }}
        />
    );
}

export default WaveformThumbnail;
